import React from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

interface Upload {
  id: number;
  video_title: string;
  upload_date: string | null;
  scheduled: boolean;
  youtube_id: string | null;
  youtube_edit: string;
  youtube_watch: string;
}

interface UploadHistoryTableProps {
  uploads: Upload[];
  statusFilter: string;
  orderBy: 'video_title' | 'upload_date';
  order: 'asc' | 'desc';
  onSort: (property: 'video_title' | 'upload_date') => void;
  onSortChange?: (orderBy: 'video_title' | 'upload_date', order: 'asc' | 'desc') => void;
}

type UploadStatus = 'upcoming' | 'archived-scheduled' | 'archived-instant';

function getUploadStatus(upload: Upload): UploadStatus {
  const now = new Date();
  const uploadDate = upload.upload_date ? new Date(upload.upload_date) : null;
  
  // Upcoming: scheduled=true AND (upload_date is null OR upload_date is in the future)
  if (upload.scheduled) {
    if (!uploadDate || uploadDate > now) {
      return 'upcoming';
    }
    // If scheduled and upload_date is in the past, it's archived-scheduled
    return 'archived-scheduled';
  }
  
  // For instant uploads (scheduled=false):
  // If upload_date exists and is in the past, it's archived-instant
  // If upload_date is null, treat as archived-instant (instant uploads that have been processed)
  if (uploadDate && uploadDate <= now) {
    return 'archived-instant';
  }
  
  // Fallback: instant upload with null date or future date (shouldn't happen, but treat as archived)
  return 'archived-instant';
}

function getStatusDisplay(status: UploadStatus): string {
  switch (status) {
    case 'upcoming':
      return 'Upcoming';
    case 'archived-scheduled':
      return 'Archived > Scheduled';
    case 'archived-instant':
      return 'Archived > Instant';
    default:
      return 'Unknown';
  }
}

function UploadHistoryTable({ uploads, statusFilter, orderBy, order, onSort, onSortChange }: UploadHistoryTableProps) {

  const SortIcon = ({ column }: { column: 'video_title' | 'upload_date' }) => {
    if (orderBy !== column) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    return order === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  // Mobile sort dropdown handlers
  const handleSortByChange = (value: string) => {
    if (onSortChange) {
      onSortChange(value as 'video_title' | 'upload_date', order);
    } else {
      onSort(value as 'video_title' | 'upload_date');
    }
  };

  const handleOrderChange = (value: string) => {
    if (onSortChange) {
      onSortChange(orderBy, value as 'asc' | 'desc');
    } else {
      // If no onSortChange, toggle the order by clicking the same column
      if (order === 'asc') {
        onSort(orderBy);
      } else {
        onSort(orderBy);
      }
    }
  };

  return (
    <Card className="upload-management-table-container">
      {/* Mobile-only sort dropdown */}
      <div className="mobile-sort-controls p-4 border-b">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <Label htmlFor="mobile-sort-by" className="text-sm font-medium">Sort By</Label>
            <Select value={orderBy} onValueChange={handleSortByChange}>
              <SelectTrigger id="mobile-sort-by" className="w-full">
                <SelectValue placeholder="Select sort field" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="video_title">Title</SelectItem>
                <SelectItem value="upload_date">Upload Date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="mobile-sort-order" className="text-sm font-medium">Order</Label>
            <Select value={order} onValueChange={handleOrderChange}>
              <SelectTrigger id="mobile-sort-order" className="w-full">
                <SelectValue placeholder="Select order" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asc">Ascending</SelectItem>
                <SelectItem value="desc">Descending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table className="upload-management-table">
          <TableHeader>
            <TableRow>
              <TableHead className="cursor-pointer" onClick={() => onSort('video_title')}>
                <div className="flex items-center">
                  Title
                  <SortIcon column="video_title" />
                </div>
              </TableHead>
              <TableHead className="cursor-pointer text-center" onClick={() => onSort('upload_date')}>
                <div className="flex items-center justify-center">
                  Upload Date
                  <SortIcon column="upload_date" />
                </div>
              </TableHead>
              <TableHead className="text-center">Type</TableHead>
              <TableHead className="text-center">Edit Link</TableHead>
              <TableHead className="text-center">Watch Link</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {uploads.length > 0 ? (
              uploads.map((upload, idx) => (
                <TableRow key={upload.id || idx}>
                  <TableCell className="font-medium">{upload.video_title}</TableCell>
                  <TableCell className="text-center">
                    {upload.upload_date
                      ? new Date(upload.upload_date).toLocaleString()
                      : 'N/A'}
                  </TableCell>
                  <TableCell className="text-center">
                    {getStatusDisplay(getUploadStatus(upload))}
                  </TableCell>
                  <TableCell className="text-center">
                    {upload.youtube_edit ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(upload.youtube_edit, '_blank')}
                      >
                        Edit
                      </Button>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {upload.youtube_watch ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(upload.youtube_watch, '_blank')}
                      >
                        Watch
                      </Button>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  No uploads found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

export default UploadHistoryTable;
